import React, { useState, useEffect, useCallback } from 'react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import useAnalytics from '../../hooks/useAnalytics';

/**
 * CredentialsTutorial - Feature 034
 * Guided overlay for the Credentials Management page, organized by topic so
 * users can jump straight to what they need instead of clicking through
 * everything. Opens on a topic menu (`SECTIONS`); picking one starts a short,
 * scoped walkthrough of just that topic's steps. Each step highlights a real
 * control (via `data-tutorial="<id>"` attributes) with a spotlight + tooltip
 * card. Never mutates credential data - steps flagged `requiresDetailsModal`
 * ask the parent (via `onRequestDetailsModal`) to open the real, read-only
 * Credential Details modal (for the first credential in the list) so the
 * "Credential Details" topic can walk through Access Keys, Enable and
 * Permissions directly; the modal is closed again once that topic ends or the
 * user returns to the menu. Falls back to a plain text explanation when a
 * target control isn't present in the DOM (e.g. empty credentials list, or a
 * conditional element like the Enable banner that isn't shown for this
 * particular credential).
 */
const STEPS = [
  {
    id: 'add-credential',
    title: 'Add New Credential',
    body: 'Creates a brand-new credential for the selected project: description, quota, alert threshold, point(s) of contact, and the APIs it should access (Certificate Provisioning is exclusive and can\u2019t be combined with other APIs). Access key + secret are shown once \u2014 copy, download the CSV, or open the documentation link, you can\u2019t view them again.',
  },
  {
    id: 'filters',
    title: 'Filters',
    body: 'Narrow the list by description, project, quota, threshold, point of contact or expiration status.',
  },
  {
    id: 'favorite',
    title: 'Favorites',
    body: 'Click the star to pin a credential to the top of the list (stored locally in your browser).',
    fallbackBody: 'Once you have a credential, you\u2019ll see a star here to pin it to the top of the list.',
  },
  {
    id: 'expiration-status',
    title: 'Expiration Status',
    body: 'Shows OK, Expiring soon (within 30 days), Expired, or No expiry for each credential. Click the column header to sort by how soon each one expires.',
    fallbackBody: 'Once you have a credential, you\u2019ll see its expiration status here.',
  },
  {
    id: 'view-details',
    title: 'View & Edit Details',
    body: 'Opens the credential\u2019s details: description, alert threshold, point(s) of contact and IP whitelist. The same modal also has Products, Access Keys, an Enable banner and Permissions \u2014 see the other topics for those.',
    fallbackBody: 'Once you have a credential, you\u2019ll see a View & Edit Details icon here.',
  },
  {
    id: 'detail-brands',
    title: 'Products',
    requiresDetailsModal: true,
    body: 'Shows the project(s)/product(s) this credential belongs to. Add or remove them from the dropdown \u2014 changing this triggers an IAM policy update on the platform, and shows a change request id you can check the status of.',
  },
  {
    id: 'detail-access-keys',
    title: 'Access Keys',
    requiresDetailsModal: true,
    body: 'Shows each access key\u2019s status (Active/Inactive) and last-used date. After a rotation, once you\u2019ve confirmed the new key works, you can remove the old one here \u2014 with a warning if it was used in the last 24 hours.',
  },
  {
    id: 'detail-enable',
    title: 'Enable',
    requiresDetailsModal: true,
    body: 'If a credential has no active access key, a banner appears here letting you re-enable it.',
  },
  {
    id: 'detail-permissions',
    title: 'Permissions',
    requiresDetailsModal: true,
    body: 'Lists the APIs this credential currently has access to. Add new ones from the dropdown, or remove existing ones \u2014 Certificate Provisioning is an exclusive choice and can\u2019t be combined with other APIs.',
  },
  {
    id: 'increase-quota',
    title: 'Increase Quota',
    body: 'Raises the certificate quota for this credential.',
    fallbackBody: 'Once you have a credential, you\u2019ll see an Increase Quota icon here.',
  },
  {
    id: 'rotate-credentials',
    title: 'Rotate Credentials',
    body: 'Generates a brand-new access key + secret (365-day policy). The new secret is shown once. AWS allows at most 2 keys per credential \u2014 if rotation fails with "already has 2 keys", clean up the old key first via View & Edit Details.',
    fallbackBody: 'Once you have a credential, you\u2019ll see a Rotate Credentials icon here.',
  },
  {
    id: 'gpg-key',
    title: 'Update GPG Key',
    body: 'Updates the GPG Armored public key for this credential\u2019s logical user. Paste a valid Armored public key block (starts with the BEGIN PGP PUBLIC KEY BLOCK header) \u2014 it\u2019s validated before submit. After a successful update you can rotate every physical user linked to this logical user so their new credentials are encrypted with the new key. Replaces the old PROC081 L2 ticket.',
    fallbackBody: 'Once you have a credential, you\u2019ll see an Update GPG Key icon here.',
  },
  {
    id: 'delete-credential',
    title: 'Permanently Delete Credential',
    body: 'Two-step deletion: first cleans up access keys, then deletes the credential. If it has 2 access keys, the cleanup step may only remove one \u2014 retry, or clean up keys manually from the details view first.',
    fallbackBody: 'Once you have a credential, you\u2019ll see a Delete icon here.',
  },
];

// Groups the steps above into short, self-contained *use cases* (not just UI areas) so
// users can jump directly to the workflow they need instead of clicking through
// everything. Step order within each section follows the real workflow order (e.g.
// rotate first, then clean up the old key), not the order steps are declared above.
const SECTIONS = [
  {
    id: 'creating',
    title: 'Creating Credentials',
    description: 'Request a new credential and its APIs.',
    stepIds: ['add-credential'],
  },
  {
    id: 'finding',
    title: 'Finding Credentials',
    description: 'Filter the list and pin favorites.',
    stepIds: ['filters', 'favorite', 'expiration-status'],
  },
  {
    id: 'viewing',
    title: 'Viewing & Editing Details',
    description: 'Description, contacts, IP whitelist and Products.',
    stepIds: ['view-details', 'detail-brands'],
  },
  {
    id: 'rotating',
    title: 'Rotating & Cleaning Up Keys',
    description: 'Update the GPG key, generate a new key, then remove the old one.',
    stepIds: ['gpg-key', 'rotate-credentials', 'detail-access-keys'],
  },
  {
    id: 'permissions',
    title: 'Managing API Permissions',
    description: 'Grant or remove access to APIs.',
    stepIds: ['detail-permissions'],
  },
  {
    id: 'other',
    title: 'Other Credential Actions',
    description: 'Enable, increase quota, or delete.',
    stepIds: ['detail-enable', 'increase-quota', 'delete-credential'],
  },
];

const CredentialsTutorial = ({ isOpen, onClose, hasCredentials, onRequestDetailsModal, onShowWrittenGuide }) => {
  // sectionId === null means the topic menu is showing; otherwise the tour is scoped
  // to that section's steps only, so users never have to click through every topic.
  const [sectionId, setSectionId] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const { setAnalytics } = useAnalytics();

  const activeSection = SECTIONS.find((s) => s.id === sectionId) || null;
  // Follow the section's stepIds order (the real workflow order), not STEPS' declaration order.
  const activeSteps = activeSection
    ? activeSection.stepIds.map((id) => STEPS.find((s) => s.id === id)).filter(Boolean)
    : [];
  const step = activeSteps[stepIndex];
  const isLastStep = !!step && stepIndex === activeSteps.length - 1;

  const recomputeTarget = useCallback(() => {
    if (!step) return;
    const el = document.querySelector(`[data-tutorial="${step.id}"]`);
    if (el) {
      // The target may be inside a scrollable container (e.g. the details modal) and
      // out of view - scroll it into view first so the spotlight/tooltip stay on screen.
      const rect = el.getBoundingClientRect();
      const isOutOfView = rect.top < 0 || rect.bottom > window.innerHeight;
      if (isOutOfView) {
        el.scrollIntoView({ block: 'center' });
        setTargetRect(el.getBoundingClientRect());
      } else {
        setTargetRect(rect);
      }
    } else {
      setTargetRect(null);
    }
  }, [step]);

  // Steps inside the credential details modal (Access Keys, Enable, Permissions) need the
  // real modal to be open. Ask the parent (CredentialsManagement) to open/close it as the
  // tour enters/leaves those steps (including when returning to the topic menu).
  useEffect(() => {
    if (!isOpen || !onRequestDetailsModal) return;
    onRequestDetailsModal(!!step?.requiresDetailsModal);
  }, [isOpen, step, onRequestDetailsModal]);

  useEffect(() => () => {
    if (onRequestDetailsModal) onRequestDetailsModal(false);
  }, [onRequestDetailsModal]);

  // The details modal's content (Access Keys, Permissions) loads asynchronously and can
  // change size after the target first appears (e.g. "Loading permissions..." swaps for
  // the actual list) - keep recomputing for as long as the step is active, not just until
  // the first non-null rect, so the spotlight follows the final, settled layout.
  useEffect(() => {
    if (!isOpen || !step?.requiresDetailsModal) return undefined;
    const interval = setInterval(recomputeTarget, 250);
    return () => clearInterval(interval);
  }, [isOpen, step, recomputeTarget]);

  useEffect(() => {
    if (!isOpen) return undefined;
    recomputeTarget();
    window.addEventListener('resize', recomputeTarget);
    window.addEventListener('scroll', recomputeTarget, true);
    return () => {
      window.removeEventListener('resize', recomputeTarget);
      window.removeEventListener('scroll', recomputeTarget, true);
    };
  }, [isOpen, recomputeTarget]);

  // Reset back to the topic menu whenever the tutorial is (re)opened.
  useEffect(() => {
    if (isOpen) {
      setSectionId(null);
      setStepIndex(0);
      setAnalytics({
        event: 'button_clicked',
        extraParams: { name: 'certificate_management-provisioning-tutorial-start' }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSelectSection = (id) => {
    setAnalytics({
      event: 'button_clicked',
      extraParams: { name: `certificate_management-provisioning-tutorial-topic-${id}` }
    });
    setSectionId(id);
    setStepIndex(0);
  };

  const handleBackToTopics = () => {
    setAnalytics({
      event: 'button_clicked',
      extraParams: { name: 'certificate_management-provisioning-tutorial-topics' }
    });
    setSectionId(null);
    setStepIndex(0);
  };

  const handleNext = () => {
    setAnalytics({
      event: 'button_clicked',
      extraParams: { name: 'certificate_management-provisioning-tutorial-next' }
    });
    if (isLastStep) {
      setAnalytics({
        event: 'button_clicked',
        extraParams: { name: 'certificate_management-provisioning-tutorial-finish' }
      });
      handleBackToTopics();
      return;
    }
    setStepIndex((prev) => prev + 1);
  };

  const handleBack = () => {
    setAnalytics({
      event: 'button_clicked',
      extraParams: { name: 'certificate_management-provisioning-tutorial-back' }
    });
    if (stepIndex === 0) {
      handleBackToTopics();
      return;
    }
    setStepIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleSkip = () => {
    setAnalytics({
      event: 'button_clicked',
      extraParams: { name: 'certificate_management-provisioning-tutorial-skip' }
    });
    setSectionId(null);
    setStepIndex(0);
    onClose();
  };

  // Topic menu: pick a section instead of clicking through every step.
  if (!activeSection) {
    return (
      <div className="fixed inset-0 z-50" role="dialog" aria-label="Guided tutorial - choose a topic">
        <div
          className="absolute inset-0 bg-black bg-opacity-50"
          onClick={handleSkip}
          onKeyDown={(e) => { if (e.key === 'Escape') handleSkip(); }}
          role="button"
          tabIndex={-1}
          aria-label="Close tutorial"
        />
        <div
          className="absolute bg-white rounded-lg shadow-xl w-96 p-5"
          style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
        >
          <div className="flex justify-between items-start mb-1">
            <h3 className="font-semibold text-gray-900 text-lg">What do you need help with?</h3>
            <button onClick={handleSkip} className="text-gray-400 hover:text-gray-600" aria-label="Close tutorial">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <p className="text-sm text-gray-500 mb-4">Pick a topic to jump straight to it.</p>
          <div className="space-y-2">
            {SECTIONS.map((section) => (
              <button
                key={section.id}
                onClick={() => handleSelectSection(section.id)}
                className="w-full text-left px-3 py-2.5 rounded-md border border-gray-200 hover:border-indigo-400 hover:bg-indigo-50"
              >
                <div className="font-medium text-gray-900 text-sm">{section.title}</div>
                <div className="text-xs text-gray-500">{section.description}</div>
              </button>
            ))}
          </div>
          {onShowWrittenGuide && (
            <button
              onClick={onShowWrittenGuide}
              className="w-full text-center text-xs text-gray-400 hover:text-indigo-600 mt-4"
            >
              Prefer reading? View the written guide instead
            </button>
          )}
        </div>
      </div>
    );
  }

  const showFallback = !hasCredentials && step.fallbackBody && !targetRect;
  const padding = 6;

  // Position the tooltip card near the target (below if there's room, above otherwise),
  // clamped so it never overflows the viewport - or centered if no target (fallback).
  let cardStyle = {};
  if (targetRect && !showFallback) {
    const estimatedCardHeight = 280;
    const spaceBelow = window.innerHeight - targetRect.bottom;
    const spaceAbove = targetRect.top;
    let top = spaceBelow >= spaceAbove
      ? targetRect.bottom + padding + 12
      : targetRect.top - padding - 12 - estimatedCardHeight;
    top = Math.min(Math.max(top, 16), window.innerHeight - estimatedCardHeight - 16);
    const left = Math.min(Math.max(targetRect.left, 16), window.innerWidth - 336);
    cardStyle = { top, left };
  }

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-label={`Guided tutorial - ${activeSection.title}`}>
      {/* Dimmed backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={handleSkip}
        onKeyDown={(e) => { if (e.key === 'Escape') handleSkip(); }}
        role="button"
        tabIndex={-1}
        aria-label="Close tutorial"
      />

      {/* Spotlight highlight box around the real target element */}
      {targetRect && !showFallback && (
        <div
          className="absolute border-2 border-indigo-400 rounded-md pointer-events-none transition-all duration-200"
          style={{
            top: targetRect.top - padding,
            left: targetRect.left - padding,
            width: targetRect.width + padding * 2,
            height: targetRect.height + padding * 2,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)',
          }}
        />
      )}

      {/* Step card */}
      <div
        className="absolute bg-white rounded-lg shadow-xl w-80 p-4"
        style={targetRect && !showFallback ? cardStyle : { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
      >
        <div className="flex items-start justify-between mb-0.5">
          <button
            onClick={handleBackToTopics}
            className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
          >
            &larr; Topics
          </button>
          <button onClick={handleSkip} className="text-gray-400 hover:text-gray-600" aria-label="Skip tutorial">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="flex justify-between items-start mb-2">
          <h3 className="font-semibold text-gray-900">{step.title}</h3>
        </div>
        {step.requiresDetailsModal && (
          <div className="text-xs text-indigo-700 bg-indigo-50 border border-indigo-100 rounded px-2 py-1 mb-2">
            Inside <strong>View &amp; Edit Details</strong> {'\u2014'} opened here for you; normally you click the eye icon on a credential row to get here.
          </div>
        )}
        <p className="text-sm text-gray-700 mb-4">
          {showFallback ? step.fallbackBody : step.body}
        </p>
        <div className="space-y-2">
          <span className="text-xs text-gray-400">{activeSection.title} {'\u2022'} {stepIndex + 1} / {activeSteps.length}</span>
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={handleBack}
              className="px-3 py-1.5 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
            >
              Back
            </button>
            <button
              onClick={handleSkip}
              className="px-3 py-1.5 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
            >
              Skip
            </button>
            <button
              onClick={handleNext}
              className="px-3 py-1.5 text-sm text-white bg-indigo-600 hover:bg-indigo-700 rounded-md"
            >
              {isLastStep ? 'Done' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CredentialsTutorial;
